import type { ReactNode } from 'react';
import { Button } from './ui/Button.js';

export function ConfirmDialog({
  title,
  body,
  warn,
  confirmText = 'Confirm',
  danger = true,
  onConfirm,
  onClose,
}: {
  title: string;
  body: string;
  warn?: string;
  confirmText?: string;
  danger?: boolean;
  onConfirm: () => void;
  onClose: () => void;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4" onClick={onClose}>
      <div
        className="w-full max-w-md rounded-lg p-6"
        style={{ background: 'var(--color-surface)', boxShadow: 'var(--shadow)' }}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-base font-semibold text-[var(--color-ink)]">{title}</h2>
        <p className="mt-2 text-sm text-[var(--color-ink-muted)]">{body}</p>
        {warn ? (
          <p
            className="mt-3 rounded-md px-3 py-2 text-xs text-[var(--color-danger)]"
            style={{
              background: 'color-mix(in srgb, var(--color-danger) 8%, transparent)',
              border: '1px solid color-mix(in srgb, var(--color-danger) 30%, transparent)',
            }}
          >
            {warn}
          </p>
        ) : null}
        <div className="mt-5 flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant={danger ? 'danger' : 'primary'}
            onClick={onConfirm}
            style={
              danger
                ? { background: 'var(--color-danger)', color: '#ffffff' }
                : undefined
            }
          >
            {confirmText}
          </Button>
        </div>
      </div>
    </div>
  );
}

export function Modal({
  title,
  children,
  footer,
  onClose,
}: {
  title: string;
  children: ReactNode;
  footer?: ReactNode;
  onClose: () => void;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4" onClick={onClose}>
      <div
        className="w-full max-w-lg rounded-lg"
        style={{ background: 'var(--color-surface)', boxShadow: 'var(--shadow)' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-[var(--color-line)] px-5 py-3">
          <h2 className="text-sm font-semibold">{title}</h2>
          <button
            type="button"
            className="rounded p-1 text-[var(--color-ink-faint)] hover:bg-[var(--color-surface-3)]"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>
        <div className="px-5 py-4">{children}</div>
        {footer ? (
          <div className="flex justify-end gap-2 border-t border-[var(--color-line)] px-5 py-3">
            {footer}
          </div>
        ) : null}
      </div>
    </div>
  );
}

export function Card({ children, className = '' }: { children: ReactNode; className?: string }) {
  return (
    <div
      className={`rounded-lg border border-[var(--color-line)] bg-[var(--color-surface)] ${className}`}
    >
      {children}
    </div>
  );
}

export function Badge({
  children,
  tone = 'neutral',
}: {
  children: ReactNode;
  tone?: 'neutral' | 'good' | 'warn' | 'bad' | 'accent';
}) {
  const cls =
    tone === 'good'
      ? 'bg-[color-mix(in_srgb,var(--color-accent)_12%,transparent)] text-[var(--color-accent)]'
      : tone === 'bad'
        ? 'bg-[color-mix(in_srgb,var(--color-danger)_12%,transparent)] text-[var(--color-danger)]'
        : tone === 'warn'
          ? 'bg-[color-mix(in_srgb,var(--color-star)_18%,transparent)] text-[var(--color-star)]'
          : tone === 'accent'
            ? 'bg-[var(--color-accent-soft)] text-[var(--color-accent)]'
            : 'bg-[var(--color-surface-3)] text-[var(--color-ink-muted)]';
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ${cls}`}>
      {children}
    </span>
  );
}

export function PageTitle({ title, sub }: { title: string; sub?: string }) {
  return (
    <div className="mb-6 pt-1">
      <h1 className="text-lg font-semibold">{title}</h1>
      {sub ? <p className="mt-1 text-sm text-[var(--color-ink-muted)]">{sub}</p> : null}
    </div>
  );
}

export function SectionLabel({ children }: { children: ReactNode }) {
  return (
    <div className="mb-2 mt-6 text-[11px] font-semibold tracking-wide text-[var(--color-ink-faint)] uppercase">
      {children}
    </div>
  );
}
